import React, { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Modal, Row, Table } from "react-bootstrap";
import axiosInstance from "../../utils/axiosUtil";
import LoadingBox from "../layout/LoadingBox";
import MessageBox from "../layout/MessageBox";
import EditReportForm from "./EditReportForm";
import CustomPagination from "../layout/CustomPagination";
import { useNavigate } from "react-router-dom";

const formatDateTime = (value) => {
    if (!value) return "N/A";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString();
};

const initialModalState = {
    show: false,
    reportId: "",
    report: null,
    loading: false,
    saving: false,
    error: "",
    validationErrors: {}
};

const defaultPagination = {
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 0
};

export default function ReportArchive() {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [actionError, setActionError] = useState("");
    const [actionMessage, setActionMessage] = useState("");
    const [updatingId, setUpdatingId] = useState("");
    const [deletingId, setDeletingId] = useState("");
    const [editModal, setEditModal] = useState(initialModalState);
    const [pagination, setPagination] = useState(defaultPagination);
    const [search, setSearch] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [exporting, setExporting] = useState(false);
    const navigate = useNavigate()

    const loadReports = async (page = 1) => {
        setLoading(true);
        setError("");
        setActionError("");
        setActionMessage("");
        try {
            const params = new URLSearchParams();
            params.set("page", String(page));
            params.set("limit", String(pagination.limit));
            if (search.trim()) {
                params.set("search", search.trim());
            }
            const { data } = await axiosInstance.get(`/reports?${params.toString()}`);

            if (data?.items && data?.pagination) {
                const normalized = Array.isArray(data.items)
                    ? data.items
                        .filter((item) => item?.isDeleted !== true)
                        .map((item) => ({
                            ...item,
                            isActive: item.isActive !== false,
                            isDeleted: item.isDeleted === true
                        }))
                    : [];
                setReports(normalized);
                setPagination(data.pagination);
            } else {
                // Fallback for old API format (no pagination)
                const normalized = Array.isArray(data)
                    ? data
                        .filter((item) => item?.isDeleted !== true)
                        .map((item) => ({
                            ...item,
                            isActive: item.isActive !== false,
                            isDeleted: item.isDeleted === true
                        }))
                    : [];
                setReports(normalized);
                setPagination({
                    page: 1,
                    limit: normalized.length,
                    total: normalized.length,
                    totalPages: 1
                });
            }
        } catch (err) {
            setError(err?.response?.data?.message || "Failed to load generated reports");
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        loadReports(newPage);
    };

    useEffect(() => {
        loadReports(1);
    }, [search]);

    const handleStatusToggle = async (reportId, nextStatus) => {
        setActionError("");
        setActionMessage("");
        setUpdatingId(reportId);
        try {
            await axiosInstance.patch(`/reports/${reportId}/status`, { isActive: nextStatus });
            setReports((prev) =>
                prev.map((item) =>
                    item._id === reportId
                        ? {
                            ...item,
                            isActive: nextStatus
                        }
                        : item
                )
            );
            setActionMessage(`Report marked as ${nextStatus ? "active" : "inactive"}.`);
        } catch (err) {
            setActionError(err?.response?.data?.message || "Failed to update report status");
        } finally {
            setUpdatingId("");
        }
    };

    const openEditModal = async (report) => {
        if (!report) return;
        setEditModal({
            ...initialModalState,
            show: true,
            reportId: report._id,
            loading: true
        });
        setActionError("");
        setActionMessage("");
        try {
            const { data } = await axiosInstance.get(`/reports/${report._id}`);
            setEditModal((prev) => ({
                ...prev,
                loading: false,
                report: {
                    ...data,
                    title: data?.title || "",
                    industry: data?.industry || "",
                    marketName: data?.marketName || "",
                    template: data?.template || "",
                    usedModel: data?.usedModel || "",
                    content: data?.content || {},
                    faqs: data?.faqs || [],
                    charts: data?.charts || [],
                    keyMetrics: data?.keyMetrics || null,
                    structuredSegments: data?.structuredSegments || {},
                    seoMetadata: data?.seoMetadata || {},
                    players: Array.isArray(data?.players) ? data.players : (Array.isArray(report?.players) ? report.players : []),
                }
            }));
        } catch (err) {
            setEditModal((prev) => ({
                ...prev,
                loading: false,
                error: err?.response?.data?.message || "Failed to load report details"
            }));
        }
    };

    const closeEditModal = () => {
        setEditModal(initialModalState);
    };

    const handleFormChange = (updatedReport) => {
        setEditModal((prev) => ({
            ...prev,
            report: updatedReport,
            validationErrors: {}
        }));
    };

    const validateForm = () => {
        const errors = {};
        const { report } = editModal;

        if (!report?.title || !report.title.trim()) {
            errors.title = "Title is required.";
        }

        if (!report?.industry || !report.industry.trim()) {
            errors.industry = "Industry is required.";
        }

        if (report?.faqs && Array.isArray(report.faqs)) {
            report.faqs.forEach((faq, index) => {
                if (faq.question && !faq.question.trim()) {
                    errors[`faq_${index}`] = `FAQ ${index + 1} question cannot be empty.`;
                }
            });
        }

        return errors;
    };

    const saveEditChanges = async () => {
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setEditModal((prev) => ({ ...prev, validationErrors, error: "Please fix validation errors before saving." }));
            return;
        }

        setEditModal((prev) => ({ ...prev, saving: true, error: "", validationErrors: {} }));
        setActionError("");
        setActionMessage("");

        try {
            const { report } = editModal;

            const payload = {
                title: report.title.trim(),
                industry: report.industry.trim(),
                marketName: report.marketName || "",
                content: report.content || {},
                charts: report.charts || [],
                faqs: report.faqs || [],
                keyMetrics: report.keyMetrics !== undefined ? report.keyMetrics : null,
                seoMetadata: report.seoMetadata !== undefined ? report.seoMetadata : null,
                structuredSegments: report.structuredSegments !== undefined ? report.structuredSegments : null,
                players: Array.isArray(report.players) ? report.players : []
            };

            const { data } = await axiosInstance.patch(`/reports/${editModal.reportId}`, payload);
            setReports((prev) =>
                prev.map((item) =>
                    item._id === data._id
                        ? {
                            ...item,
                            ...data,
                            isActive: data.isActive !== false,
                            isDeleted: data.isDeleted === true
                        }
                        : item
                )
            );
            setActionMessage("Report details updated successfully.");
            closeEditModal();
        } catch (err) {
            const message =
                err instanceof Error
                    ? err.message
                    : err?.response?.data?.message || "Failed to update report details";
            setEditModal((prev) => ({ ...prev, error: message }));
        } finally {
            setEditModal((prev) => ({ ...prev, saving: false }));
        }
    };

    const handleSoftDelete = async (reportId) => {
        if (!window.confirm("Are you sure you want to hide this report?")) {
            return;
        }
        setActionError("");
        setActionMessage("");
        setDeletingId(reportId);
        try {
            await axiosInstance.patch(`/reports/${reportId}/delete`, { isDeleted: true });
            setReports((prev) => prev.filter((item) => item._id !== reportId));
            setActionMessage("Report hidden successfully. It will no longer appear in the archive.");
        } catch (err) {
            setActionError(err?.response?.data?.message || "Failed to delete report");
        } finally {
            setDeletingId("");
        }
    };

    const handleExport = async () => {
        try {
            setExporting(true);
            const params = new URLSearchParams();
            if (fromDate) params.set("from", fromDate);
            if (toDate) params.set("to", toDate);

            const response = await axiosInstance.get(`/reports/export?${params.toString()}`, {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Reports_Export_${new Date().getTime()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setActionError("Failed to export Excel. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const truncateTitle = (title = "", limit = 48) => {
        if (!title) return "Untitled report";
        if (title.length <= limit) return title;
        return `${title.slice(0, limit).trim()}…`;
    };

    const toTitleCase = (value = "") =>
        value
            .toString()
            .trim()
            .toLowerCase()
            .replace(/\s+/g, " ")
            .split(" ")
            .filter(Boolean)
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");

    const deriveMarketName = (report) => {
        const direct = report?.marketName && report.marketName.trim() ? report.marketName.trim() : "";
        if (direct) return toTitleCase(direct);

        const title = (report?.title || "").toString().trim();
        if (!title) return "Untitled report";

        // Heuristic fallback: keep the market phrase (e.g., "Global Fly Ash Market")
        // Remove common suffixes like "Size", "Share", "Forecast...", and trailing punctuation.
        let cleaned = title
            .replace(/\s*\|.*$/g, "")
            .replace(/,\s*(size|share|forecast).*$/i, "")
            .replace(/\s*(size|share|forecast).*$/i, "")
            .trim();

        // Ensure it ends with "Market" if the title contains it elsewhere
        const marketMatch = cleaned.match(/^(.*?\bmarket\b)/i);
        if (marketMatch && marketMatch[1]) cleaned = marketMatch[1].trim();

        return toTitleCase(cleaned);
    };

    return (
        <Container fluid className="page-section py-4">
            <Row className="mb-4">
                <Col md={8}>
                    <h3 className="page-title">Report Archive</h3>
                    <p className="page-subtitle mb-0">
                        Browse the Gemini-generated reports. Manage visibility and fine-tune the stored content before it reaches the
                        public site.
                    </p>
                </Col>
                <Col md={4} className="mt-3 mt-md-0 d-flex justify-content-md-end align-items-center">
                    <Button variant="light" className="btn-ghost" onClick={() => loadReports(pagination.page)} disabled={loading}>
                        Refresh list
                    </Button>
                </Col>
            </Row>

            <Row className="mb-4 align-items-end">
                <Col md={3}>
                    <Form.Group>
                        <Form.Label className="small fw-bold text-muted text-uppercase">Search</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="Search market..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </Form.Group>
                </Col>
                {/* Commented out for now
        <Col md={2}>
          <Form.Group>
            <Form.Label className="small fw-bold text-muted text-uppercase">From Date</Form.Label>
            <Form.Control
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label className="small fw-bold text-muted text-uppercase">To Date</Form.Label>
            <Form.Control
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exporting}
            className="w-100"
          >
            {exporting ? "Exporting..." : "Export to Excel"}
          </Button>
        </Col>
        */}
            </Row>

            <Row>
                <Col>
                    <Card>
                        <Card.Body>
                            <Card.Title>Latest generated reports</Card.Title>
                            <div className="card-divider" />
                            {loading ? (
                                <div className="py-4 text-center">
                                    <LoadingBox />
                                </div>
                            ) : error ? (
                                <MessageBox variant="danger">{error}</MessageBox>
                            ) : reports.length === 0 ? (
                                <p className="text-muted mb-0">No reports generated yet.</p>
                            ) : (
                                <>
                                    {actionMessage && (
                                        <MessageBox variant="success" className="mb-3">
                                            {actionMessage}
                                        </MessageBox>
                                    )}
                                    {actionError && (
                                        <MessageBox variant="warning" className="mb-3">
                                            {actionError}
                                        </MessageBox>
                                    )}
                                    <Table responsive hover className="mt-3 align-middle">
                                        <thead>
                                            <tr>
                                                <th>Title</th>
                                                <th>Industry</th>
                                                <th>View</th>
                                                <th>Generated</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {reports.map((report) => {
                                                const isActive = report.isActive !== false;
                                                return (
                                                    <tr key={report._id}>
                                                        <td className="fw-semibold">
                                                            {report.marketName || report.title}
                                                        </td>
                                                        <td>{report.industry}</td>
                                                        <td>
                                                            <Button
                                                                variant="light"
                                                                className="btn-ghost-info btn-sm"
                                                                href={`https://www.makdatainsights.com/reports/${report.slug}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                View
                                                            </Button>
                                                        </td>
                                                        <td>{formatDateTime(report.createdAt)}</td>
                                                        <td className="text-end">
                                                            <div className="d-flex justify-content-end align-items-center gap-3 flex-wrap">
                                                                <div className="d-flex align-items-center gap-2">
                                                                    <Badge bg={isActive ? "success" : "secondary"}>
                                                                        {isActive ? "Active" : "Inactive"}
                                                                    </Badge>
                                                                    <Form.Check
                                                                        type="switch"
                                                                        id={`report-status-${report._id}`}
                                                                        className="status-toggle"
                                                                        label=""
                                                                        checked={isActive}
                                                                        disabled={updatingId === report._id || deletingId === report._id}
                                                                        onChange={(event) => handleStatusToggle(report._id, event.target.checked)}
                                                                    />
                                                                </div>
                                                                <Button
                                                                    variant="light"
                                                                    className="btn-ghost-primary btn-sm"
                                                                    onClick={() =>
                                                                        navigate(`/admin/reports/edit/${report.slug}`)
                                                                    }
                                                                >
                                                                    Edit details
                                                                </Button>
                                                                <Button
                                                                    variant="light"
                                                                    className="btn-ghost-danger btn-sm"
                                                                    disabled={deletingId === report._id}
                                                                    onClick={() => handleSoftDelete(report._id)}
                                                                >
                                                                    {deletingId === report._id
                                                                        ? "Deleting..."
                                                                        : "Delete report"}
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                    {pagination.totalPages > 1 && (
                                        <CustomPagination
                                            pages={pagination.totalPages}
                                            curPage={pagination.page}
                                            pageHandler={handlePageChange}
                                        />
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Modal show={editModal.show} onHide={closeEditModal} centered size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>Edit report details</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                    {editModal.loading ? (
                        <div className="py-4 text-center">
                            <LoadingBox />
                        </div>
                    ) : editModal.report ? (
                        <>
                            {editModal.error && <MessageBox variant="danger" className="mb-3">{editModal.error}</MessageBox>}
                            <EditReportForm
                                report={editModal.report}
                                onChange={handleFormChange}
                                errors={editModal.validationErrors}
                            />
                        </>
                    ) : (
                        <MessageBox variant="danger">Failed to load report data.</MessageBox>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeEditModal} disabled={editModal.saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={saveEditChanges}
                        disabled={editModal.saving || editModal.loading || !editModal.report}
                    >
                        {editModal.saving ? "Saving..." : "Save changes"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
}

